import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Users2, Plus, Trash2, AlertTriangle, LayoutGrid, TableProperties, ArrowRightCircle, Phone,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import { useDebounce } from "@/hooks/useDebounce";
import LoadingState from "@/components/ui/loading-state";
import { formatTanggal, hariIniISO } from "@/lib/tanggal";
import { useCabangOptions } from "@/lib/cabang";
import {
  PageHeader, TidakPunyaAkses, SearchInput, SortTh, useSortState, sortRows, usePaginasi,
  Paginasi, unduhCsv, TombolEksporCsv, Modal, Field, inputCls, selectCls, textareaCls,
  InputCabang, useCampaignMap, namaCampaign, usePinHapus, PinHapusDialog,
  FilterSelect, FilterRentangTanggal, TombolResetFilter, useOpsiFilter,
  adaFilterAktif, paramFilter,
  LEAD_STAGES, LEAD_STAGE_LABEL, LEAD_STAGE_COLOR,
} from "./_shared";

const SUMBER_SARAN = ["Instagram Ads", "TikTok Ads", "Google Ads", "Referral", "WhatsApp", "Walk-in", "Website", "Lainnya"];

const emptyForm = () => ({ nama: "", no_hp: "", sumber: "", campaign_id: "", cabang: "", catatan: "" });

export default function Leads() {
  const { can } = useAuth();
  const bolehLihat = can("marketing_lead", "view");
  const bolehTambah = can("marketing_lead", "create");
  const bolehEdit = can("marketing_lead", "update");
  const bolehHapus = can("marketing_lead", "delete");
  const bolehCampaign = can("marketing_campaign", "view");
  const { campaignMap, campaignList } = useCampaignMap(bolehCampaign);
  const cabangOpsi = useCabangOptions({ denganKosong: true });
  const [searchParams] = useSearchParams();
  const campaignPreset = searchParams.get("campaign_id") || "";

  const [view, setView] = useState("kanban");
  const [q, setQ] = useState("");
  const qDebounced = useDebounce(q, 350);
  const [campaignFilter, setCampaignFilter] = useState(campaignPreset);
  const [staleOnly, setStaleOnly] = useState(false);
  const [stageFilter, setStageFilter] = useState("");
  const [sumberFilter, setSumberFilter] = useState("");
  const [cabangFilter, setCabangFilter] = useState("");
  const [pembuatFilter, setPembuatFilter] = useState("");
  const [dari, setDari] = useState("");
  const [sampai, setSampai] = useState("");
  const { sortKey, sortDir, toggle } = useSortState("created_at", "desc");
  const [form, setForm] = useState(null);
  const [pindahState, setPindahState] = useState(null); // {lead, stage, catatan, sale_id}
  const [busy, setBusy] = useState(false);
  const { dialog: pinDialog, setDialog: setPinDialog, hapusDenganPin, submitDialog } = usePinHapus();
  const opsi = useOpsiFilter("/marketing/leads/opsi-filter", bolehLihat);

  // SEMUA filter dikirim ke server — daftar ini dipotong 5000 baris di backend,
  // jadi menyaringnya di sini membuat lead ke-5001 tidak pernah ketemu dan
  // layarnya melapor "tidak ada hasil" dengan yakin (CLAUDE.md).
  const { data, loading, error, refetch } = useDataQuery(
    ["marketing", "leads", campaignFilter, staleOnly, stageFilter, qDebounced,
      sumberFilter, cabangFilter, pembuatFilter, dari, sampai],
    () => api.get("/marketing/leads", {
      params: paramFilter({
        campaign_id: campaignFilter, stale: staleOnly ? true : "", stage: stageFilter,
        q: qDebounced.trim(), sumber: sumberFilter, cabang: cabangFilter,
        dibuat_oleh: pembuatFilter, dari, sampai,
      }),
    }).then((r) => r.data),
    { enabled: bolehLihat },
  );

  const rows = useMemo(() => data || [], [data]);
  const sorted = sortRows(rows, sortKey, sortDir);
  const { page, setPage, totalPages, awal, akhir } = usePaginasi(sorted.length, 15);
  const halaman = sorted.slice(awal, akhir);

  const filterAktif = adaFilterAktif(q, campaignFilter, staleOnly, stageFilter,
                                     sumberFilter, cabangFilter, pembuatFilter, dari, sampai);
  const resetFilter = () => {
    setQ(""); setCampaignFilter(""); setStaleOnly(false); setStageFilter("");
    setSumberFilter(""); setCabangFilter(""); setPembuatFilter(""); setDari(""); setSampai("");
  };

  const byStage = useMemo(() => {
    const m = {}; LEAD_STAGES.forEach((s) => { m[s] = []; });
    rows.forEach((l) => { (m[l.stage] || (m[l.stage] = [])).push(l); });
    return m;
  }, [rows]);

  const eksporCsv = () => {
    unduhCsv(`leads-${hariIniISO()}.csv`, [
      { label: "Kode", value: (r) => r.code },
      { label: "Nama", value: (r) => r.nama },
      { label: "No HP", value: (r) => r.no_hp },
      { label: "Sumber", value: (r) => r.sumber },
      { label: "Campaign", value: (r) => namaCampaign(campaignMap, r.campaign_id) },
      { label: "Cabang", value: (r) => r.cabang || "" },
      { label: "Stage", value: (r) => LEAD_STAGE_LABEL[r.stage] || r.stage },
      { label: "Mengendap >48 Jam", value: (r) => (r.is_stale ? "Ya" : "Tidak") },
      { label: "Dibuat", value: (r) => r.created_at },
    ], sorted);
  };

  const simpan = async () => {
    if (!form.nama.trim()) { toast.error("Nama lead wajib diisi"); return; }
    setBusy(true);
    try {
      const r = await api.post("/marketing/leads", { ...form, campaign_id: form.campaign_id || null });
      toast.success(r.data.message || "Lead ditambahkan");
      setForm(null); refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah lead"); }
    finally { setBusy(false); }
  };

  const submitPindah = async () => {
    setBusy(true);
    try {
      await api.patch(`/marketing/leads/${pindahState.lead.id}/stage`, {
        stage: pindahState.stage, catatan: pindahState.catatan, sale_id: pindahState.sale_id || undefined,
      });
      toast.success("Stage lead diperbarui");
      setPindahState(null); refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memindah stage lead"); }
    finally { setBusy(false); }
  };

  const hapus = async (l) => {
    if (!window.confirm(`Hapus lead "${l.nama}"?`)) return;
    const aksi = (pin) => api.delete(`/marketing/leads/${l.id}`, pin ? { params: { pin } } : undefined);
    const langsung = await hapusDenganPin(aksi, { modul: "marketing_lead", targetId: l.id, ringkasan: `Hapus lead ${l.nama}` });
    if (langsung) { toast.success("Lead dihapus"); refetch(); }
  };

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 space-y-5">
        <PageHeader icon={Users2} title="Lead & Atribusi" />
        <TidakPunyaAkses label="Lead & Atribusi" testid="mkt-leads-tanpa-akses" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-leads">
      <PageHeader
        icon={Users2} title="Lead & Atribusi"
        desc="Pipeline lead per stage — lead yang mengendap >48 jam di Baru/Follow Up ditandai merah."
        actions={<>
          <TombolEksporCsv onClick={eksporCsv} testid="mkt-leads-export" />
          {bolehTambah && (
            <button onClick={() => setForm(emptyForm())} data-testid="mkt-leads-tambah"
                    className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Tambah Lead
            </button>
          )}
        </>}
      />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <SearchInput value={q} onChange={setQ} placeholder="Cari nama/no HP/kode/catatan..." testid="mkt-leads-cari" className="w-56" />
          {bolehCampaign && (
            <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} data-testid="mkt-leads-filter-campaign" className={`${selectCls} h-9 w-auto`}>
              <option value="">Semua Campaign</option>
              {campaignList.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.nama}</option>)}
            </select>
          )}
          {/* "Status" versi Lead = stage pipeline-nya. */}
          <FilterSelect value={stageFilter} onChange={setStageFilter} testid="mkt-leads-filter-stage"
                        semuaLabel="Semua Stage"
                        opsi={LEAD_STAGES.map((s) => ({ value: s, label: LEAD_STAGE_LABEL[s] }))} />
          {/* "Platform" versi Lead = sumber/channel asalnya (Instagram Ads, Referral, ...). */}
          <FilterSelect value={sumberFilter} onChange={setSumberFilter} testid="mkt-leads-filter-sumber"
                        semuaLabel="Semua Sumber" opsi={opsi.sumber} />
          <FilterSelect value={cabangFilter} onChange={setCabangFilter} testid="mkt-leads-filter-cabang"
                        semuaLabel="Semua Cabang" opsi={opsi.cabang} />
          <FilterSelect value={pembuatFilter} onChange={setPembuatFilter} testid="mkt-leads-filter-pembuat"
                        semuaLabel="Semua User" opsi={opsi.dibuat_oleh} />
          <FilterRentangTanggal dari={dari} sampai={sampai} onDari={setDari} onSampai={setSampai}
                                testid="mkt-leads-filter-tanggal"
                                labelDari="Masuk dari" labelSampai="Masuk s/d" />
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 h-9 px-2">
            <input type="checkbox" checked={staleOnly} onChange={(e) => setStaleOnly(e.target.checked)} data-testid="mkt-leads-filter-stale" className="w-3.5 h-3.5 accent-rose-600" />
            Hanya yang mengendap &gt;48 jam
          </label>
          <TombolResetFilter aktif={filterAktif} onReset={resetFilter} testid="mkt-leads-reset-filter" />
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 gap-1">
          <button onClick={() => setView("kanban")} data-testid="mkt-leads-view-kanban"
                  className={`px-3 h-8 rounded-md text-xs font-medium inline-flex items-center gap-1.5 ${view === "kanban" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            <LayoutGrid className="w-3.5 h-3.5" /> Kanban
          </button>
          <button onClick={() => setView("tabel")} data-testid="mkt-leads-view-tabel"
                  className={`px-3 h-8 rounded-md text-xs font-medium inline-flex items-center gap-1.5 ${view === "tabel" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            <TableProperties className="w-3.5 h-3.5" /> Tabel
          </button>
        </div>
      </div>

      {view === "kanban" ? (
        <LoadingState loading={loading} error={error} onRetry={refetch} varian="kartu" jumlahKartu={6} data-testid="mkt-leads-kanban-loading">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="mkt-leads-kanban">
            {LEAD_STAGES.map((stage) => (
              <div key={stage} className={`rounded-xl border-2 ${LEAD_STAGE_COLOR[stage]} p-2.5 flex flex-col gap-2 min-h-[140px]`} data-testid={`mkt-leads-kolom-${stage}`}>
                <div className="flex items-center justify-between px-1">
                  <p className="text-xs font-semibold text-slate-700">{LEAD_STAGE_LABEL[stage]}</p>
                  <span className="text-[11px] font-mono text-slate-500">{byStage[stage]?.length || 0}</span>
                </div>
                <div className="space-y-2">
                  {(byStage[stage] || []).map((l) => (
                    <div key={l.id} data-testid={`mkt-leads-card-${l.id}`}
                         className={`bg-white rounded-lg border p-2.5 shadow-sm space-y-1 ${l.is_stale ? "border-rose-300 ring-1 ring-rose-200" : "border-slate-200"}`}>
                      <div className="flex items-start justify-between gap-1">
                        <p className="text-xs font-semibold text-slate-900">{l.nama}</p>
                        {l.is_stale && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 shrink-0">
                            <AlertTriangle className="w-2.5 h-2.5" /> &gt;48j
                          </span>
                        )}
                      </div>
                      {l.no_hp && <p className="text-[11px] text-slate-500 inline-flex items-center gap-1"><Phone className="w-2.5 h-2.5" /> {l.no_hp}</p>}
                      <p className="text-[11px] text-slate-400">{l.sumber || "-"}{l.campaign_id ? ` · ${namaCampaign(campaignMap, l.campaign_id)}` : ""}</p>
                      <div className="flex items-center justify-between pt-1">
                        <p className="text-[10px] text-slate-400">{formatTanggal(l.stage_entered_at, { denganJam: true })}</p>
                        {bolehEdit && (
                          <button onClick={() => setPindahState({ lead: l, stage: l.stage, catatan: "", sale_id: "" })}
                                  data-testid={`mkt-leads-pindah-${l.id}`}
                                  className="text-[10px] font-medium text-emerald-700 hover:underline inline-flex items-center gap-0.5">
                            <ArrowRightCircle className="w-3 h-3" /> Pindah
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {(byStage[stage] || []).length === 0 && <p className="text-[11px] text-slate-400 text-center py-4">Kosong</p>}
                </div>
              </div>
            ))}
          </div>
        </LoadingState>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <SortTh label="Nama" sortKeyName="nama" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-leads-sort-nama" />
                  <th className="px-3 py-2.5 font-semibold">No HP / Sumber</th>
                  <th className="px-3 py-2.5 font-semibold">Campaign</th>
                  <th className="px-3 py-2.5 font-semibold">Stage</th>
                  <th className="px-3 py-2.5 font-semibold">Cabang</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <LoadingState loading={loading} error={error} onRetry={refetch} kosong={!loading && !error && halaman.length === 0}
                              varian="tabel" kolom={6} pesanKosong="Belum ada lead." data-testid="mkt-leads-tabel-state">
                  {halaman.map((l) => (
                    <tr key={l.id} className="hover:bg-slate-50/60" data-testid={`mkt-leads-row-${l.id}`}>
                      <td className="px-3 py-2.5">
                        <p className="font-mono text-[11px] text-slate-400">{l.code}</p>
                        <p className="font-medium text-slate-900">{l.nama}</p>
                        {l.is_stale && <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 mt-0.5"><AlertTriangle className="w-2.5 h-2.5" /> Mengendap &gt;48 jam</span>}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs">{l.no_hp || "-"}<br />{l.sumber || "-"}</td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs">{namaCampaign(campaignMap, l.campaign_id)}</td>
                      <td className="px-3 py-2.5">
                        <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full border ${LEAD_STAGE_COLOR[l.stage]}`}>{LEAD_STAGE_LABEL[l.stage]}</span>
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs">{l.cabang || "-"}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-end gap-1">
                          {bolehEdit && (
                            <button onClick={() => setPindahState({ lead: l, stage: l.stage, catatan: "", sale_id: "" })} data-testid={`mkt-leads-t-pindah-${l.id}`}
                                    className="px-2 h-7 rounded border border-slate-200 hover:bg-slate-50 text-slate-600 text-[11px] font-medium">Pindah Stage</button>
                          )}
                          {bolehHapus && (
                            <button onClick={() => hapus(l)} data-testid={`mkt-leads-hapus-${l.id}`}
                                    className="w-7 h-7 rounded hover:bg-rose-50 text-rose-600 grid place-items-center"><Trash2 className="w-3.5 h-3.5" /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </LoadingState>
              </tbody>
            </table>
          </div>
          <Paginasi page={page} setPage={setPage} totalPages={totalPages} testid="mkt-leads-paginasi" />
        </div>
      )}

      {/* Form tambah lead */}
      <Modal open={!!form} onClose={() => setForm(null)} title="Tambah Lead" testid="mkt-leads-modal"
             footer={<>
               <button onClick={() => setForm(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
               <button onClick={simpan} disabled={busy} data-testid="mkt-leads-simpan"
                       className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">{busy ? "Menyimpan..." : "Simpan"}</button>
             </>}>
        {form && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nama" required>
                <input value={form.nama} onChange={(e) => setForm((f) => ({ ...f, nama: e.target.value }))} data-testid="mkt-leads-f-nama" className={inputCls} />
              </Field>
              <Field label="No HP / WhatsApp">
                <input value={form.no_hp} onChange={(e) => setForm((f) => ({ ...f, no_hp: e.target.value }))} data-testid="mkt-leads-f-hp" className={inputCls} inputMode="tel" />
              </Field>
            </div>
            <Field label="Sumber">
              <input value={form.sumber} list="mkt-leads-sumber-saran" onChange={(e) => setForm((f) => ({ ...f, sumber: e.target.value }))} data-testid="mkt-leads-f-sumber" className={inputCls} placeholder="cth. Instagram Ads" />
              <datalist id="mkt-leads-sumber-saran">{SUMBER_SARAN.map((s) => <option key={s} value={s} />)}</datalist>
            </Field>
            {bolehCampaign && (
              <Field label="Campaign (opsional)">
                <select value={form.campaign_id} onChange={(e) => setForm((f) => ({ ...f, campaign_id: e.target.value }))} data-testid="mkt-leads-f-campaign" className={selectCls}>
                  <option value="">— tidak terkait campaign —</option>
                  {campaignList.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.nama}</option>)}
                </select>
              </Field>
            )}
            <Field label="Cabang">
              <InputCabang value={form.cabang} onChange={(v) => setForm((f) => ({ ...f, cabang: v }))} opsi={cabangOpsi} testid="mkt-leads-f-cabang" />
            </Field>
            <Field label="Catatan">
              <textarea value={form.catatan} rows={2} onChange={(e) => setForm((f) => ({ ...f, catatan: e.target.value }))} data-testid="mkt-leads-f-catatan" className={textareaCls} />
            </Field>
          </>
        )}
      </Modal>

      {/* Modal pindah stage */}
      <Modal open={!!pindahState} onClose={() => setPindahState(null)} title={pindahState ? `Pindah Stage — ${pindahState.lead.nama}` : ""} maxWidth="max-w-sm" testid="mkt-leads-pindah-modal"
             footer={<>
               <button onClick={() => setPindahState(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
               <button onClick={submitPindah} disabled={busy} data-testid="mkt-leads-pindah-simpan"
                       className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">{busy ? "Menyimpan..." : "Pindahkan"}</button>
             </>}>
        {pindahState && (
          <>
            <Field label="Stage Tujuan" required>
              <select value={pindahState.stage} onChange={(e) => setPindahState((s) => ({ ...s, stage: e.target.value }))} data-testid="mkt-leads-pindah-stage" className={selectCls}>
                {LEAD_STAGES.map((s) => <option key={s} value={s}>{LEAD_STAGE_LABEL[s]}</option>)}
              </select>
            </Field>
            {pindahState.stage === "closed_won" && (
              <Field label="ID Sale Konversi (opsional)" hint="Isi kalau lead ini sudah tercatat sebagai penjualan di POS">
                <input value={pindahState.sale_id} onChange={(e) => setPindahState((s) => ({ ...s, sale_id: e.target.value }))} data-testid="mkt-leads-pindah-sale" className={inputCls} />
              </Field>
            )}
            <Field label="Catatan">
              <textarea value={pindahState.catatan} rows={2} onChange={(e) => setPindahState((s) => ({ ...s, catatan: e.target.value }))} data-testid="mkt-leads-pindah-catatan" className={textareaCls} />
            </Field>
          </>
        )}
      </Modal>

      <PinHapusDialog dialog={pinDialog} setDialog={setPinDialog} onSubmit={() => submitDialog(refetch)} />
    </div>
  );
}
